import { GrainUsageTypeEnum, PlatformTypeEnum } from '@/common/constants';
import { ContractStatusesEnum } from '@/common/constants/contract.statuses';
import { PartiesEnum } from '@/common/constants/parties.constants';
import SelectControl from '@/components/controls/select-control';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import usePartyGrainStore from '@/features/parties-grain/store/usePartyGrainStore';
import { SelectParties } from '@/features/parties/models';
import { usePartyStore } from '@/features/parties/store';
import useStore from '@/lib/store';
import { yupResolver } from '@hookform/resolvers/yup';
import { EditIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { FormPartiesValues } from '../../models';
import { partiesSchema } from '../../schemas/parties.schema';
import { useLoadStore } from '../../store';
import useMeasureStore from '../general/store/useMeasureStore';

export const EditParties = () => {
  const { t } = useTranslation();
  const form = useForm<FormPartiesValues>({
    mode: 'onChange',
    defaultValues: {
      party_id: '',
      harvest_type: '',
    },
    resolver: yupResolver(partiesSchema),
  });

  const harvest_type = useWatch({
    control: form.control,
    name: 'harvest_type',
  });
  const { user } = useStore();
  const [openDialog, setOpenDialog] = useState(false);
  const { fetchPartiesSelect, partiesSelectGeneral } = usePartyStore();
  const {
    partiesGrainSelect,
    partiesGrainSelectGoods,
    fetchPartiesSelectGrain,
  } = usePartyGrainStore();
  const {
    load,
    loadGrain,
    fetchDataDetail,
    manageParties,
    isManagePartiesLoading,
    fetchGrainDataDetail,
    manageGrainParties,
    isManageGrainPartiesLoading,
  } = useLoadStore();
  const { harvestType } = useMeasureStore();
  const isCotton = user?.platform_type === PlatformTypeEnum.COTTON;

  const isDeleted =
    load?.farmer_contract_status === ContractStatusesEnum.DELETED ||
    load?.farmer_contract_status === ContractStatusesEnum.CANCELED;

  useEffect(() => {
    if (harvest_type !== '' && openDialog) {
      fetchPartiesSelect(undefined, harvest_type);
    }
  }, [harvest_type, openDialog]);

  useEffect(() => {
    if (!isCotton && loadGrain) {
      fetchPartiesSelectGrain({
        usage_type: loadGrain?.usage_type,
        plant_id: loadGrain?.plant_id,
        crop_type_id: loadGrain?.crop_type_id,
        crop_generation_id: loadGrain?.crop_generation_id,
      });
    }
  }, [
    isCotton,
    loadGrain?.usage_type,
    loadGrain?.plant_id,
    loadGrain?.crop_type_id,
    loadGrain?.crop_generation_id,
  ]);

  const Label2 = (party: SelectParties): string => {
    const typeLabel =
      party?.generation === 'TECHNICAL'
        ? 'Texnik'
        : `Urug'lik (Avlodi: ${party?.crop_generation_name})`;

    return [
      `${party.place_number} - ${party.party_number}`,
      `(Seleksion navi: ${party?.crop_type_name}`,
      `Sanoat navi: ${party?.industrial_grade}`,
      `Paxta turi: ${typeLabel})`,
    ].join(', ');
  };

  const party_list = useMemo(() => {
    return partiesSelectGeneral
      ?.filter((v) => {
        if (v?.generation !== load?.crop_generation_type) return false;
        if (v?.cluster_id !== load?.cluster_id) return false;
        if (v?.harvest_type !== harvest_type) return false;
        if (v?.generation === PartiesEnum.SEED) {
          if (v?.crop_generation_id !== load?.crop_generation_id) return false;
          if (v?.storage_type !== load?.storage_type) return false;
        }
        return true;
      })
      ?.map((party) => ({
        label: `${party?.place_number} - ${party?.party_number} - (Sanoat navi:${party?.industrial_grade} sinfi:${party.class})`,
        label2: Label2(party),
        value: party?.id,
      }));
  }, [partiesSelectGeneral, load]);

  const onSubmit: SubmitHandler<FormPartiesValues> = async (data) => {
    try {
      if (isCotton) {
        const payload: Record<string, string> = {};
        if (data.party_id) payload.party_id = data.party_id;
        if (data.harvest_type) payload.harvest_type = data.harvest_type;

        await manageParties(payload, load?.id);
        await fetchDataDetail(load?.id!);
      } else {
        const payload: Record<string, string> = {};
        if (data.party_id) payload.party_id = data.party_id;

        await manageGrainParties(payload, loadGrain?.id);
        await fetchGrainDataDetail(loadGrain?.id!);
      }

      setOpenDialog(false);
      toast.success(t('data.saved'));
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Form {...form}>
      <Dialog onOpenChange={setOpenDialog} open={openDialog}>
        <DialogTrigger asChild>
          <Button
            size="icon"
            className="border-grey-500 w-5 bg-transparent"
            variant="ghost"
            disabled={isDeleted}
          >
            <EditIcon className="w-5" color="green" />
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[625px]">
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>{t('edit.parties')}</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-3 my-5">
              {isCotton && (
                <SelectControl
                  control={form.control}
                  name="harvest_type"
                  label={t('harvest.type')}
                  options={harvestType}
                  placeholder={t('choose.data')}
                  disableSort
                />
              )}
              {isCotton ? (
                <SelectControl
                  name="party_id"
                  label={t('party')}
                  options={party_list}
                  control={form.control}
                  placeholder={t('choose.data')}
                  labelProp="label2"
                />
              ) : (
                <SelectControl
                  name="party_id"
                  label={
                    loadGrain?.usage_type === GrainUsageTypeEnum.SEED
                      ? t('party')
                      : t('place')
                  }
                  options={
                    loadGrain?.usage_type === GrainUsageTypeEnum.SEED
                      ? partiesGrainSelect
                      : partiesGrainSelectGoods
                  }
                  control={form.control}
                  placeholder={t('choose.data')}
                />
              )}
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline"> {t('cancel')}</Button>
              </DialogClose>
              <Button
                type="submit"
                disabled={!form.formState.isValid}
                isLoading={
                  isCotton
                    ? isManagePartiesLoading
                    : isManageGrainPartiesLoading
                }
              >
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Form>
  );
};
